import {
  Badge,
  Button,
  HStack,
  Image,
  Td,
  Text,
  Input,
  InputGroup,
  InputLeftElement,
  useToast,
} from "@chakra-ui/react";
import { Group, GroupProvider } from "../../../types/Group";
import { StyledTable } from "../../../components/StyledTable/StyledTable";
import { assetIcons } from "../../../constants/icons";
import {
  useAcceptGroupRequest,
  useGetProvidersInGroup,
  useRemoveUserFromGroup,
  useSetGroupProviderPayoutEnabled,
  useSetGroupProviderRole,
  useSetGroupRVUTackerOptIn,
  useSetGroupProviderTargetingEnabled,
  useSetGroupProviderLnqEnabled,
  useSetGroupRVUTrackerVisibility,
  useSetGroupProviderFixedUsdPerRvu,
} from "../../../api/GroupApi";
import { ChangeEvent, useMemo, useState, useCallback, useEffect } from "react";
import { THEME_COLORS } from "../../../base/theme/foundations/colors";
import { useUserData } from "../../../api/UserApi";
import ProvidersTableRowMenu from "./ProvidersTableRowMenu";
import Switch from "../../../components/Switch/Switch";
import { Pagination } from "../../../components/Pagination/Pagination";
import CustomSelect from "../../../components/Select/Select";
import debounce from "lodash/debounce";
import { useNavigate } from "react-router-dom";

type RVUTrackerVisibility = "view_all" | "view_self" | "none";

interface Props {
  group: Group;
  isGroupAdmin: boolean;
}

export const ProvidersTable = ({ group, isGroupAdmin }: Props) => {
  const { data: currentUser } = useUserData();
  const [perPage] = useState(30);
  const [page, setPage] = useState(1);
  const [inputValues, setInputValues] = useState<Record<string, string>>({});
  const { data, isLoading: isGettingProvidersInGroup } = useGetProvidersInGroup(
    group.id,
    page,
    perPage,
    {},
  );
  const { mutate: acceptGroupRequest, isPending: isAcceptingGroupRequest } =
    useAcceptGroupRequest();
  const { mutate: removeUserFromGroup, isPending: isRemovingUserFromGroup } =
    useRemoveUserFromGroup();
  const { mutate: setGroupProviderRole } = useSetGroupProviderRole();
  const { mutate: setGroupRVUTackerOptIn } = useSetGroupRVUTackerOptIn();
  const { mutate: setGroupProviderPayoutEnabled } = useSetGroupProviderPayoutEnabled();
  const { mutate: setGroupProviderTargetingEnabled } =
    useSetGroupProviderTargetingEnabled();
  const { mutate: setGroupProviderLnqEnabled } = useSetGroupProviderLnqEnabled();
  const { mutate: setGroupRVUTrackerVisibility } = useSetGroupRVUTrackerVisibility();
  const { mutate: setGroupProviderFixedUsdPerRvu } = useSetGroupProviderFixedUsdPerRvu();
  const toast = useToast();
  const navigate = useNavigate();

  const groupProviders = data?.providers;

  const providerAdminTableColumns = [
    { key: "provider", label: "Provider" },
    { key: "available", label: "Available" },
    { key: "role", label: "Role" },
    { key: "fixedUsdPerRvu", label: "Fixed RVU" },
    { key: "payoutEnabled", label: "Payout enabled" },
    { key: "tracker", label: "RVU Tracker" },
    { key: "targeting", label: "Targeting enabled" },
    { key: "lnqEnabled", label: "Create LnQ" },
    { key: "status", label: "Application status" },
    {
      key: "rvuTrackerVisibility",
      label: (
        <span
          style={{ display: "block", textAlign: "left", width: "100%", paddingLeft: 32 }}
        >
          RVU Tracker
          <br />
          Visibility
        </span>
      ),
    },
    { key: "actions", label: "" },
  ];

  const providerTableColumns = [
    { key: "provider", label: "Provider" },
    { key: "available", label: "Available" },
    { key: "role", label: "Role" },
  ];

  const providersData = useMemo(() => {
    return groupProviders
      ?.map((provider) => {
        return {
          ...provider,
          provider: `${provider.user.lastName} ${provider.user.firstName}`,
          isAvailable: provider.isAvailable,
          available: provider.isAvailable ? "Yes" : "No",
          status:
            provider.user.accountStatus === "temp_password" ||
            provider.user.accountStatus === "pending_first_login"
              ? "Invited"
              : provider.status === "pending"
                ? "Pending"
                : "Approved",
        };
      })
      .filter((provider) => {
        if (isGroupAdmin) return true;
        return !isGroupAdmin && provider.status === "Approved";
      });
  }, [groupProviders, isGroupAdmin]);

  const approveProvider = (id: string) => {
    acceptGroupRequest({ id, groupId: group.id });
  };

  const rejectProvider = (id: string) => {
    removeUserFromGroup({ id, groupId: group.id });
  };

  const navigateToProvider = (groupProviderId: string) => {
    navigate(`/groups/provider/${groupProviderId}`);
  };

  const removeProvider = (id: string) => {
    removeUserFromGroup({ id, groupId: group.id });
  };

  const setGroupProviderRoleForGroup = (
    groupProviderId: string,
    role: "admin" | "member",
  ) => {
    setGroupProviderRole({ groupProviderId, groupId: group.id, role });
  };

  const handleOnRVUTracker =
    (groupProviderId: string) => (e: ChangeEvent<HTMLInputElement>) => {
      setGroupRVUTackerOptIn({
        groupProviderId,
        groupId: group.id,
        rvuTrackerOptIn: e.target.checked,
      });
    };

  const handleOnPayoutEnabledToggle =
    (groupProviderId: string) => (e: ChangeEvent<HTMLInputElement>) => {
      setGroupProviderPayoutEnabled({
        groupProviderId,
        groupId: group.id,
        payoutEnabled: e.target.checked,
      });
    };

  const handleOnTargetingEnabledToggle =
    (groupProviderId: string) => (e: ChangeEvent<HTMLInputElement>) => {
      setGroupProviderTargetingEnabled({
        groupProviderId,
        groupId: group.id,
        targetingEnabled: e.target.checked,
      });
    };

  const handleOnLnqEnabledToggle =
    (groupProviderId: string) => (e: ChangeEvent<HTMLInputElement>) => {
      setGroupProviderLnqEnabled({
        groupProviderId,
        groupId: group.id,
        lnqEnabled: e.target.checked,
      });
    };

  const debouncedSetFixedUsdPerRvu = useCallback(
    debounce((groupProviderId: string, value: number) => {
      setGroupProviderFixedUsdPerRvu({
        groupProviderId,
        groupId: group.id,
        fixedUsdPerRvuEnabled: true,
        fixedUsdPerRvu: value,
      });
    }, 500),
    [group.id],
  );

  useEffect(() => {
    return () => {
      debouncedSetFixedUsdPerRvu.cancel();
    };
  }, [debouncedSetFixedUsdPerRvu]);

  const handleFixedUsdPerRvuChange =
    (groupProviderId: string) => (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setInputValues((prev) => ({ ...prev, [groupProviderId]: value }));

      const numValue = parseFloat(value);
      if (!isNaN(numValue)) {
        if (numValue === 0) {
          setGroupProviderFixedUsdPerRvu({
            groupProviderId,
            groupId: group.id,
            fixedUsdPerRvuEnabled: false,
            fixedUsdPerRvu: 0,
          });
        } else {
          debouncedSetFixedUsdPerRvu(groupProviderId, numValue);
          setGroupProviderFixedUsdPerRvu({
            groupProviderId,
            groupId: group.id,
            fixedUsdPerRvuEnabled: true,
            fixedUsdPerRvu: numValue,
          });
        }
      }
    };

  const handleFixedUsdPerRvuToggle =
    (groupProviderId: string, row: GroupProvider) => (e: ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.checked;
      const rawValue = inputValues[groupProviderId] ?? row.fixedUsdPerRvu;
      const currentAmount = parseFloat(rawValue ?? "0");
      if (newValue && (!rawValue || currentAmount === 0)) {
        toast({
          title: "Enter a value greater than 0 to enable Fixed RVU",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      setGroupProviderFixedUsdPerRvu({
        groupProviderId,
        groupId: group.id,
        fixedUsdPerRvuEnabled: newValue,
        fixedUsdPerRvu: currentAmount,
      });
    };

  const isButtonDisabled = () => {
    return isAcceptingGroupRequest ?? isRemovingUserFromGroup;
  };

  const groupRow = (row: GroupProvider) => {
    return (
      <>
        <Td>
          <Text textStyle={"smMdSemi"}>
            {row.provider}
            {row.user.disabled && (
              <Badge mx={2} colorScheme="error">
                Deleted
              </Badge>
            )}
          </Text>
        </Td>
        <Td>
          <Text textStyle={"smBold"}>{row.available}</Text>
        </Td>
        <Td>
          <Text textStyle={"smBold"}>{row.role === "admin" ? "Admin" : "Provider"}</Text>
        </Td>
        {isGroupAdmin && (
          <>
            <Td>
              <HStack>
                <Switch
                  isToggled={(row.fixedUsdPerRvu ?? 0) > 0 && !!row.fixedUsdPerRvuEnabled}
                  onToggle={handleFixedUsdPerRvuToggle(row.id, row)}
                />
                <InputGroup w="80px" ml={5}>
                  <InputLeftElement
                    pointerEvents="none"
                    color="gray.300"
                    fontWeight="bold"
                    fontSize="s"
                    children="$ "
                    h="20px"
                  />
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={inputValues[row.id] ?? row.fixedUsdPerRvu ?? ""}
                    onChange={handleFixedUsdPerRvuChange(row.id)}
                    borderRadius="16px"
                    bg="#181B34"
                    color="white"
                    borderColor="#8E959E"
                    borderWidth="1px"
                    pl={8}
                    pr={2}
                    h="20px"
                    fontWeight="500"
                    fontSize="sm"
                    _placeholder={{ color: "gray.400" }}
                    _focus={{ borderColor: "#6B73FF", boxShadow: "none" }}
                    placeholder="0.00"
                  />
                </InputGroup>
              </HStack>
            </Td>
            <Td>
              <Switch
                isToggled={row.payoutEnabled}
                onToggle={handleOnPayoutEnabledToggle(row.id)}
              />
            </Td>
            <Td>
              <Switch
                isToggled={row.rvuTrackerOptIn}
                onToggle={handleOnRVUTracker(row.id)}
              />
            </Td>
            <Td>
              <Switch
                isToggled={row.targetingEnabled}
                onToggle={handleOnTargetingEnabledToggle(row.id)}
              />
            </Td>
            <Td>
              <Switch
                isToggled={row.lnqEnabled}
                onToggle={handleOnLnqEnabledToggle(row.id)}
                disabled={isGroupAdmin && currentUser?.id === row.user.id}
              />
            </Td>
            <Td>
              <HStack>
                <Image
                  src={
                    row.status === "Pending"
                      ? assetIcons.clock
                      : row.status === "Invited"
                        ? assetIcons.arrowDown
                        : assetIcons.check
                  }
                />
                <Text
                  textStyle={"smBold"}
                  color={
                    row.status === "Pending"
                      ? THEME_COLORS.gray[500]
                      : row.status === "Invited"
                        ? "yellow.400"
                        : "#69CE53"
                  }
                >
                  {row?.status}
                </Text>
              </HStack>
            </Td>
            <Td textAlign="center" style={{ paddingLeft: 24 }}>
              <CustomSelect
                options={[
                  { value: "view_all", label: "View all" },
                  { value: "view_self", label: "View self" },
                  { value: "none", label: "None" },
                ]}
                value={row.rvuTrackerVisibility}
                onValueChange={(val) =>
                  setGroupRVUTrackerVisibility({
                    groupProviderId: row.id,
                    groupId: group.id,
                    visibility: val as RVUTrackerVisibility,
                  })
                }
                minW="140px"
              />
            </Td>
            <Td width={"25%"}>
              <HStack justify="flex-end">
                {row.status === "Pending" && (
                  <>
                    <Button
                      variant="outline"
                      color="white"
                      size="sm"
                      onClick={() => void approveProvider(row.id)}
                      isLoading={isButtonDisabled()}
                    >
                      Accept
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      borderColor="white"
                      textColor="error.400"
                      onClick={() => void rejectProvider(row.id)}
                      isLoading={isButtonDisabled()}
                    >
                      Reject
                    </Button>
                  </>
                )}
                <ProvidersTableRowMenu
                  groupProvider={row}
                  navigateToProvider={navigateToProvider}
                  removeProvider={removeProvider}
                  setGroupProviderRoleForGroup={setGroupProviderRoleForGroup}
                  currentUser={currentUser}
                  groupId={group.id}
                />
              </HStack>
            </Td>
          </>
        )}
      </>
    );
  };

  return (
    <>
      <StyledTable
        columns={isGroupAdmin ? providerAdminTableColumns : providerTableColumns}
        customRowRenderers={groupRow}
        data={providersData ?? []}
        loading={isGettingProvidersInGroup}
      />
      <Pagination pages={data?.totalPages ?? 0} currentPage={page} setPage={setPage} />
    </>
  );
};
